import { isPWAMode, isBrowserMode } from './deviceDetect';

// PWA Data Manager for handling data persistence
export class PWADataManager {
  private static instance: PWADataManager;
  private isPWA: boolean = false;
  private isBrowser: boolean = false;

  constructor() {
    this.updateMode();
  }

  static getInstance(): PWADataManager {
    if (!PWADataManager.instance) {
      PWADataManager.instance = new PWADataManager();
    }
    return PWADataManager.instance;
  }

  // Update PWA mode detection
  updateMode() {
    this.isPWA = isPWAMode();
    this.isBrowser = isBrowserMode();
    
    console.log('PWA Data Manager - Mode Updated:', {
      isPWA: this.isPWA,
      isBrowser: this.isBrowser
    });
  }

  // Check if running in PWA mode
  isRunningInPWA(): boolean {
    return this.isPWA;
  }

  // Check if running in browser mode
  isRunningInBrowser(): boolean {
    return this.isBrowser;
  }

  // Get storage quota information
  async getStorageInfo(): Promise<{
    quota: number;
    usage: number;
    available: number;
    percentage: number;
  }> {
    if (!('storage' in navigator) || !('estimate' in navigator.storage)) {
      return {
        quota: 0,
        usage: 0,
        available: 0,
        percentage: 0
      };
    }

    try {
      const estimate = await navigator.storage.estimate();
      const quota = estimate.quota || 0;
      const usage = estimate.usage || 0;
      const available = quota - usage;
      const percentage = quota > 0 ? (usage / quota) * 100 : 0;

      return {
        quota,
        usage,
        available,
        percentage: Math.round(percentage)
      };
    } catch (error) {
      console.error('Error getting storage info:', error);
      return {
        quota: 0,
        usage: 0,
        available: 0,
        percentage: 0
      };
    }
  }

  // Check if storage is available
  async isStorageAvailable(): Promise<boolean> {
    try {
      if (!('storage' in navigator) || !('persist' in navigator.storage)) {
        return false;
      }

      const isPersistent = await navigator.storage.persist();
      return isPersistent;
    } catch (error) {
      console.error('Error checking storage availability:', error);
      return false;
    }
  }

  // Request persistent storage
  async requestPersistentStorage(): Promise<boolean> {
    try {
      if (!('storage' in navigator) || !('persist' in navigator.storage)) {
        return false;
      }

      const isPersistent = await navigator.storage.persist();
      console.log('Persistent storage granted:', isPersistent);
      return isPersistent;
    } catch (error) {
      console.error('Error requesting persistent storage:', error);
      return false;
    }
  }

  // Get PWA installation status
  getInstallationStatus(): {
    isInstalled: boolean;
    mode: 'pwa' | 'browser';
    canInstall: boolean;
    needsReinstall: boolean;
  } {
    const isInstalled = this.isPWA;
    const mode = this.isPWA ? 'pwa' : 'browser';
    
    // Check if PWA needs reinstallation
    const needsReinstall = this.checkIfReinstallNeeded();

    return {
      isInstalled,
      mode,
      canInstall: !isInstalled,
      needsReinstall
    };
  }

  // Check if PWA needs reinstallation
  private checkIfReinstallNeeded(): boolean {
    // Check if service worker is registered
    if (!('serviceWorker' in navigator)) {
      return true;
    }

    // Check if manifest is accessible
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (!manifestLink) {
      return true;
    }

    // Check if running in browser mode when it should be PWA
    if (this.isBrowser && localStorage.getItem('pwa-install-dismissed') === 'true') {
      // User previously dismissed install, but might want to reinstall
      return false;
    }

    return false;
  }

  // Backup critical data
  async backupCriticalData(): Promise<boolean> {
    try {
      const criticalData = {
        userPreferences: this.getUserPreferences(),
        appState: this.getAppState(),
        timestamp: new Date().toISOString(),
        pwaMode: this.isPWA
      };

      // Store in multiple locations for redundancy
      localStorage.setItem('pwa-backup', JSON.stringify(criticalData));
      
      // Also store in IndexedDB if available
      if ('indexedDB' in window) {
        await this.storeInIndexedDB('pwa-backup', criticalData);
      }

      console.log('Critical data backed up successfully');
      return true;
    } catch (error) {
      console.error('Error backing up critical data:', error);
      return false;
    }
  }

  // Restore critical data
  async restoreCriticalData(): Promise<boolean> {
    try {
      // Try to restore from localStorage first
      const backupData = localStorage.getItem('pwa-backup');
      if (backupData) {
        const data = JSON.parse(backupData);
        this.restoreUserPreferences(data.userPreferences);
        this.restoreAppState(data.appState);
        console.log('Critical data restored from localStorage');
        return true;
      }

      // Try to restore from IndexedDB
      if ('indexedDB' in window) {
        const data = await this.getFromIndexedDB('pwa-backup');
        if (data) {
          this.restoreUserPreferences(data.userPreferences);
          this.restoreAppState(data.appState);
          console.log('Critical data restored from IndexedDB');
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Error restoring critical data:', error);
      return false;
    }
  }

  // Get user preferences
  private getUserPreferences(): any {
    return {
      theme: localStorage.getItem('theme'),
      language: localStorage.getItem('language'),
      notifications: localStorage.getItem('notifications'),
      installDismissed: localStorage.getItem('pwa-install-dismissed')
    };
  }

  // Restore user preferences
  private restoreUserPreferences(preferences: any): void {
    if (preferences.theme) localStorage.setItem('theme', preferences.theme);
    if (preferences.language) localStorage.setItem('language', preferences.language);
    if (preferences.notifications) localStorage.setItem('notifications', preferences.notifications);
    if (preferences.installDismissed) localStorage.setItem('pwa-install-dismissed', preferences.installDismissed);
  }

  // Versioned backup functionality
  async createVersionedBackup(name: string, description?: string): Promise<{ id: string; success: boolean }> {
    try {
      const backupId = `backup_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const timestamp = new Date();
      
      // Collect all critical data
      const criticalData = {
        localStorageData: this.getAllLocalStorageData(),
        userPreferences: this.getUserPreferences(),
        appState: this.getAppState(),
        timestamp,
        version: '1.0.0',
        description: description || 'Manual backup'
      };
      
      // Store backup in IndexedDB
      await this.storeInIndexedDB(`backup_${backupId}`, criticalData);
      
      // Update backup versions list
      const backupVersions = await this.getBackupVersions();
      const newBackup = {
        id: backupId,
        name,
        timestamp,
        size: JSON.stringify(criticalData).length,
        description: description || 'Manual backup'
      };
      
      backupVersions.unshift(newBackup);
      
      // Keep only last 10 backups
      if (backupVersions.length > 10) {
        const oldBackup = backupVersions.pop();
        if (oldBackup) {
          await this.deleteBackup(oldBackup.id);
        }
      }
      
      await this.storeInIndexedDB('backup_versions', backupVersions);
      
      console.log('Versioned backup created:', newBackup);
      return { id: backupId, success: true };
    } catch (error) {
      console.error('Error creating versioned backup:', error);
      return { id: '', success: false };
    }
  }
  
  async getBackupVersions(): Promise<any[]> {
    try {
      return await this.getFromIndexedDB('backup_versions') || [];
    } catch (error) {
      console.error('Error getting backup versions:', error);
      return [];
    }
  }
  
  async restoreFromBackup(backupId: string): Promise<boolean> {
    try {
      const backupData = await this.getFromIndexedDB(`backup_${backupId}`);
      if (!backupData) {
        throw new Error('Backup not found');
      }
      
      // Clear current data
      localStorage.clear();
      
      // Restore data
      if (backupData.localStorageData) {
        for (const [key, value] of Object.entries(backupData.localStorageData)) {
          localStorage.setItem(key, value as string);
        }
      }
      
      if (backupData.userPreferences) {
        this.restoreUserPreferences(backupData.userPreferences);
      }
      
      if (backupData.appState) {
        this.restoreAppState(backupData.appState);
      }
      
      console.log('Data restored from backup:', backupId);
      return true;
    } catch (error) {
      console.error('Error restoring from backup:', error);
      return false;
    }
  }
  
  async deleteBackup(backupId: string): Promise<boolean> {
    try {
      await this.deleteFromIndexedDB(`backup_${backupId}`);
      
      // Update backup versions list
      const backupVersions = await this.getBackupVersions();
      const updatedVersions = backupVersions.filter((backup: any) => backup.id !== backupId);
      await this.storeInIndexedDB('backup_versions', updatedVersions);
      
      console.log('Backup deleted:', backupId);
      return true;
    } catch (error) {
      console.error('Error deleting backup:', error);
      return false;
    }
  }
  
  private getAllLocalStorageData(): { [key: string]: string } {
    const data: { [key: string]: string } = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        data[key] = localStorage.getItem(key) || '';
      }
    }
    return data;
  }

  // Clear all data
  async clearAllData(): Promise<boolean> {
    try {
      // Clear localStorage
      localStorage.clear();
      
      // Clear IndexedDB
      await this.clearIndexedDB();
      
      // Clear service worker cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }
      
      console.log('All PWA data cleared successfully');
      return true;
    } catch (error) {
      console.error('Error clearing all data:', error);
      return false;
    }
  }

  // Clear IndexedDB
  private async clearIndexedDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase('PWAStorage');
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Get app state
  private getAppState(): any {
    return {
      lastProject: localStorage.getItem('lastProject'),
      userSession: localStorage.getItem('userSession'),
      appVersion: localStorage.getItem('appVersion')
    };
  }

  // Restore app state
  private restoreAppState(state: any): void {
    if (state.lastProject) localStorage.setItem('lastProject', state.lastProject);
    if (state.userSession) localStorage.setItem('userSession', state.userSession);
    if (state.appVersion) localStorage.setItem('appVersion', state.appVersion);
  }

  // Store data in IndexedDB
  private async storeInIndexedDB(key: string, data: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('PWAStorage', 2);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['data'], 'readwrite');
        const store = transaction.objectStore('data');
        store.put(data, key);
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('data')) {
          db.createObjectStore('data');
        }
      };
    });
  }

  // Get data from IndexedDB
  private async getFromIndexedDB(key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('PWAStorage', 2);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const db = request.result;
        
        // Check if object store exists
        if (!db.objectStoreNames.contains('data')) {
          console.log('Object store does not exist, returning null');
          resolve(null);
          return;
        }
        
        const transaction = db.transaction(['data'], 'readonly');
        const store = transaction.objectStore('data');
        const getRequest = store.get(key);
        
        getRequest.onsuccess = () => resolve(getRequest.result);
        getRequest.onerror = () => reject(getRequest.error);
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('data')) {
          db.createObjectStore('data');
        }
      };
    });
  }

  // Clear all PWA data
  async clearAllData(): Promise<boolean> {
    try {
      // Clear localStorage
      localStorage.clear();
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear IndexedDB
      if ('indexedDB' in window) {
        const request = indexedDB.deleteDatabase('PWAStorage');
        await new Promise((resolve, reject) => {
          request.onsuccess = () => resolve(true);
          request.onerror = () => reject(request.error);
        });
      }
      
      // Clear service worker cache
      if ('serviceWorker' in navigator && 'caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      console.log('All PWA data cleared');
      return true;
    } catch (error) {
      console.error('Error clearing PWA data:', error);
      return false;
    }
  }

  // Get PWA health status
  async getHealthStatus(): Promise<{
    isHealthy: boolean;
    issues: string[];
    recommendations: string[];
  }> {
    const issues: string[] = [];
    const recommendations: string[] = [];

    // Check PWA mode
    if (this.isBrowser) {
      issues.push('Running in browser mode - consider installing PWA');
      recommendations.push('Install PWA for better experience');
    }

    // Check storage availability
    const isStorageAvailable = await this.isStorageAvailable();
    if (!isStorageAvailable) {
      issues.push('Persistent storage not available');
      recommendations.push('Request persistent storage permission');
    }

    // Check storage quota
    const storageInfo = await this.getStorageInfo();
    if (storageInfo.percentage > 90) {
      issues.push('Storage quota nearly full');
      recommendations.push('Clear unnecessary data or request more storage');
    }

    // Check service worker
    if (!('serviceWorker' in navigator)) {
      issues.push('Service worker not supported');
      recommendations.push('Use a supported browser');
    }

    return {
      isHealthy: issues.length === 0,
      issues,
      recommendations
    };
  }
}

// Export singleton instance
export const pwaDataManager = PWADataManager.getInstance();
